import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { emailValidator } from '../../../../utils/validators';
import { ForceResetError } from '../../forceReset/ForceResetError';
import { useForceReset } from '../../forceReset/useForceReset';
import { RecaptchaNotice } from '../../RecaptchaNotice/RecaptchaNotice';
import { useHandleAuth } from '../../useHandleAuth';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';
import { RedesignEmailAndPasswordInputs } from '../RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { ThirdPartyAuthRedesign } from '../ThirdPartyAuthRedesign/ThirdPartyAuthRedesign';

import styles from './LogInOptionsRedesign.module.scss';

export const LogInOptionsRedesign = observer(function LogInOptionsRedesign(props: WizardChildProps) {
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [fieldsErrors, setFieldsErrors] = useState({ password: '', email: '' });

  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();
  const { isSigningUp } = authStore;

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { handleAuth: runAuth } = useHandleAuth(setSubmitError, props.nextStep);
  const { initialEmail, handleAuth, isForceReset, clearForceReset, startResetPassword } = useForceReset(runAuth);
  const [email, setEmail] = useState(initialEmail);

  const clearError = () => {
    setFieldsErrors({ password: '', email: '' });
    clearForceReset();
  };

  const isProcessing = computed(() => authStore.pending('signIn')).get();

  const openResetPassword = () =>
    startResetPassword(() =>
      openAuthWizard('resetPasswordWizard', {
        force: true,
        landingKey: landingKey ?? undefined,
      }),
    );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isProcessing || isSigningUp || isForceReset) {
      return;
    }
    const emailError = emailValidator(email.trim());

    if (emailError) {
      setFieldsErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    if (!password.trim()) {
      setFieldsErrors((prev) => ({ ...prev, password: t('auth.pleaseFillInAllRequiredFields') }));
      return;
    }

    void analytics.sendEvent('login_account', {
      event_action: 'click',
      event_category: 'sign_up',
      event_type: 'login_platform',
      event_value: 'email',
      button_text: 'log in',
    });

    void handleAuth({
      provider: 'email',
      action: 'log_in',
      credentials: {
        email,
        password,
      },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isProcessing || isSigningUp) {
      return;
    }

    const targetId = (event.target as HTMLElement)?.id;

    if (event.key === 'Enter' && (targetId === 'email' || targetId === 'password')) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const onGoBack = () => {
    portalStore.closePortal();
  };

  const goToSignUp = () => {
    void analytics.sendEvent('signup_account_button', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'sign up',
    });
    openAuthWizard('signUpWizard', {
      triggeredBy: 'log_in',
      force: true,
      landingKey: landingKey ?? undefined,
    });
  };

  useEffect(() => {
    if (props.isCurrent) {
      void analytics.sendEvent('login_page', {
        event_action: 'impression',
        event_category: 'sign_up',
      });
    }
  }, [props.isCurrent]);

  return (
    <RedesignContainer className={styles.container} onKeyDown={handleKeyDown}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="close-login"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="signin-back-button"
        headerLeftElement={<HeaderLogoElement position="left" showLogo={isDesktop} />}
        headerRightElement={<HeaderLogoElement position="right" showLogo={!isDesktop} />}
        title={t('auth.logIn')}
        {...(isDesktop ? { onClose: onGoBack } : { onGoBack })}
      />
      <DialogLoader isProcessing={isProcessing || isSigningUp} />
      {/* noValidate: handleSubmit shows our own field errors; native validation would block it first. */}
      <form noValidate className={styles.containerContent} onSubmit={handleSubmit}>
        <div className={styles.thirdPartyAuthWrapper}>
          <div className={styles.signUpOptionsSubtitleWrapper}>
            <span className={styles.signUpOptionsSubtitle}>{t('auth.logInWith')}</span>
          </div>

          <ThirdPartyAuthRedesign action="Log in" />
        </div>

        <div className={styles.rowLineContainer}>
          <div className={classNames(styles.lineGradient, styles.lineGradientLeft)} />
          <span className={styles.orEmailText}>{t('auth.orEmail')}</span>
          <div className={classNames(styles.lineGradient, styles.lineGradientRight)} />
        </div>

        <RedesignEmailAndPasswordInputs
          className={styles.emailAndPasswordInputsWrapper}
          defaultEmail={initialEmail}
          disabled={isSigningUp}
          emailError={fieldsErrors.email}
          hideErrors={true}
          passwordError={fieldsErrors.password}
          type="log_in"
          onChangeEmail={(newEmail) => {
            clearError();
            setEmail(newEmail);
          }}
          onChangePassword={(newPassword) => {
            clearError();
            setPassword(newPassword);
          }}
        />

        {isForceReset ? (
          <ForceResetError message={submitError} onResetPassword={openResetPassword} />
        ) : (
          <>
            <div
              className={classNames(styles.forgotPasswordLink, {
                'animation-slide-down': true,
                'form-links': true,
              })}
              data-testid="login-redesign-forgot-password-line"
            >
              <Trans
                components={{
                  question: <span />,
                  reset: (
                    <span className={styles.loginLink} data-testid="reset-password-btn" onClick={openResetPassword} />
                  ),
                }}
                i18nKey="auth.forgotPasswordClickHere"
              />
            </div>

            <ErrorMessage className={styles.authErrorMessage}>
              {submitError || fieldsErrors.email || fieldsErrors.password}
            </ErrorMessage>
          </>
        )}

        <div className={styles.createAccountEmailButtonWrapper}>
          <Button
            btnType="main"
            className={styles.emailSignUpButton}
            dataTestId="log-in-btn"
            disabled={isForceReset}
            type="submit"
          >
            {t('auth.logIn')}
          </Button>
        </div>

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="login-redesign-signup-line"
        >
          <Trans
            components={{
              question: <span />,
              signup: <span className={styles.loginLink} data-testid="lobby-signup-btn" onClick={goToSignUp} />,
            }}
            i18nKey="auth.noAccountYetSignUp"
          />
        </div>
        <RecaptchaNotice />
      </form>
    </RedesignContainer>
  );
});
